import React from "react";
import PropTypes from "prop-types";
import "./DivergingBars.scss";

const defaultFormat = (n) => Number(n || 0).toLocaleString("en-GB");

/**
 * DivergingBars — what moved, and which way: one bar per row growing left (down) or
 * right (up) from a shared zero. For "why did the total change?" — the answer is
 * almost never spread evenly, and a list of percentages hides which rows carried it.
 *
 * Colour is semantic (better / worse), never the row's identity: pass `invert` for a
 * measure where up is bad (bots, declines).
 *
 * @param rows  [{ key, label, value (the change), current?, previous?, swatch? }] — order is the caller's
 */
const DivergingBars = ({ rows, format = defaultFormat, invert = false, unit, emptyText = "No change to show.", limit }) => {
  const shown = limit ? rows.slice(0, limit) : rows;
  if (!shown.length) return <p className="diverging__empty">{emptyText}</p>;
  const max = Math.max(1, ...shown.map((r) => Math.abs(r.value || 0)));
  return (
    <div className="diverging">
      {shown.map((r) => {
        const v = r.value || 0;
        const good = invert ? v < 0 : v > 0;
        const w = `${(Math.abs(v) / max) * 100}%`;
        const sign = v > 0 ? "+" : v < 0 ? "−" : "±";
        return (
          <div className="diverging__row" key={r.key} title={r.previous != null ? `${r.label}: ${format(r.previous)} → ${format(r.current)}${unit ? ` ${unit}` : ""}` : undefined}>
            <span className="diverging__label">
              {r.swatch && <i style={{ background: r.swatch }} />}
              {r.label}
            </span>
            <span className="diverging__half diverging__half--neg">
              {v < 0 && <span className={`diverging__bar ${good ? "diverging__bar--good" : "diverging__bar--bad"}`} style={{ width: w }} />}
            </span>
            <span className="diverging__half diverging__half--pos">
              {v > 0 && <span className={`diverging__bar ${good ? "diverging__bar--good" : "diverging__bar--bad"}`} style={{ width: w }} />}
            </span>
            <span className={`diverging__value${v === 0 ? "" : good ? " diverging__value--good" : " diverging__value--bad"}`}>
              {sign}{format(Math.abs(v))}
            </span>
            {r.previous != null && (
              <span className="diverging__context">{format(r.previous)} → {format(r.current)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

DivergingBars.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired, label: PropTypes.node.isRequired, value: PropTypes.number.isRequired,
    current: PropTypes.number, previous: PropTypes.number, swatch: PropTypes.string,
  })).isRequired,
  format: PropTypes.func,
  invert: PropTypes.bool,
  unit: PropTypes.string,
  emptyText: PropTypes.string,
  limit: PropTypes.number,
};

export default DivergingBars;
